import React from 'react'
import { Box, Typography, Divider, Chip, Grid, Tooltip } from '@mui/material'
import VerifiedIcon from '@mui/icons-material/Verified'
import SecurityIcon from '@mui/icons-material/Security'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import FingerprintIcon from '@mui/icons-material/Fingerprint'
import DnsIcon from '@mui/icons-material/Dns'
import LockIcon from '@mui/icons-material/Lock'
import DevicesIcon from '@mui/icons-material/Devices'

/**
 * Formats an ISO date string to a human-readable date.
 * Returns '—' if null/undefined.
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

/**
 * Maps internal certificate usage keys to display labels.
 */
const usageLabel = (usage) => {
  const map = {
    'server-internal': 'Server Cert — Internal',
    'server-public': 'Server Cert — Public / Internet-Facing',
    'client-internal': 'Client Cert — Internal',
    'client-broadridge': 'Client Cert — Broadridge-Managed for External Use',
    'client-external': 'Client Cert — Externally-Managed',
    'other': 'Other',
  }
  return map[usage] || usage || '—'
}

/**
 * A single labeled field inside the certificate template.
 */
const CertField = ({ label, value, mono = false, truncate = false }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#8b9bb4',
        fontWeight: 600,
        fontSize: '0.63rem',
        mb: 0.3,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: mono ? '"Roboto Mono", "Courier New", monospace' : 'inherit',
        color: '#e2e8f0',
        fontSize: mono ? '0.75rem' : '0.875rem',
        wordBreak: truncate ? 'break-all' : 'normal',
        lineHeight: 1.5,
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
)

/**
 * VenafiCertificatePreview — Renders a faux certificate card showing all
 * details retrieved from the /vedsdk/certificates/{guid} endpoint together
 * with the fields already present in the onboarding form.
 *
 * Props:
 *   selectedMatch  — object stored in venafiLookup.selectedMatch
 *   formData       — current onboarding form data
 */
const VenafiCertificatePreview = ({ selectedMatch, formData }) => {
  if (!selectedMatch) return null

  const {
    cert_common_name,
    sans = [],
    client_name,
    certificate_usage,
    valid_from,
    valid_to,
    key_algorithm,
    key_size,
    thumbprint,
    issuer,
    serial_number,
    consumers = [],
    certificate_dn,
    policy_dn,
    contact_email_cr,
  } = selectedMatch

  const commonName = cert_common_name || formData.commonName || '—'

  // Determine if cert is expired / expiring soon
  const now = new Date()
  const expiryDate = valid_to ? new Date(valid_to) : null
  const daysUntilExpiry = expiryDate ? Math.ceil((expiryDate - now) / 86400000) : null
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry >= 0 && daysUntilExpiry <= 30

  const statusColor = isExpired ? '#f87171' : isExpiringSoon ? '#fbbf24' : '#34d399'
  const statusLabel = isExpired ? 'EXPIRED' : isExpiringSoon ? 'EXPIRING SOON' : 'VALID'

  const venafiPolicyLabels = {
    'prod-digicert':    '\\\\VED\\\\Policy\\\\Production\\\\BRCC DigiCert Standard CA\\\\Automation',
    'prod-private':     '\\\\VED\\\\Policy\\\\Production\\\\BRCC Entrust Private Gateway CA\\\\Automation',
    'nonprod-digicert': '\\\\VED\\\\Policy\\\\Non-Production\\\\BRCC DigiCert Standard CA\\\\Automation',
    'nonprod-private':  '\\\\VED\\\\Policy\\\\Non-Production\\\\BRCC Entrust Private Gateway CA\\\\Automation',
  }

  const policyBaseFull = venafiPolicyLabels[formData.venafiPolicyBase] || formData.venafiPolicyBase || '—'

  return (
    <Box
      sx={{
        position: 'relative',
        mt: 3,
        mb: 2,
        borderRadius: 3,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0f1b35 0%, #1a2c50 50%, #0f1b35 100%)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.08)',
        /* Decorative foil-like border */
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 3,
          padding: '2px',
          background: 'linear-gradient(135deg, #c9a84c, #f0d080, #c9a84c, #8b6914, #c9a84c)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Watermark-style background emblem */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: 0.04,
          pointerEvents: 'none',
        }}
      >
        <SecurityIcon sx={{ fontSize: 280, color: '#c9a84c' }} />
      </Box>

      {/* ─── HEADER ─────────────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(90deg, rgba(201,168,76,0.18) 0%, rgba(201,168,76,0.06) 100%)',
          borderBottom: '1px solid rgba(201,168,76,0.25)',
          px: 4,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #c9a84c, #f0d080)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 0 16px rgba(201,168,76,0.4)',
          }}
        >
          <LockIcon sx={{ color: '#0f1b35', fontSize: 26 }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: '0.65rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#c9a84c',
              fontWeight: 700,
              mb: 0.25,
            }}
          >
            Venafi Certificate Record
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#f0d080',
              fontWeight: 700,
              letterSpacing: '0.02em',
              lineHeight: 1.2,
              wordBreak: 'break-all',
            }}
          >
            {commonName}
          </Typography>
        </Box>

        <Chip
          label={statusLabel}
          size="small"
          sx={{
            bgcolor: 'transparent',
            border: `1px solid ${statusColor}`,
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.65rem',
            letterSpacing: '0.1em',
            flexShrink: 0,
          }}
        />
      </Box>

      {/* ─── BODY ───────────────────────────────────────────────── */}
      <Box sx={{ px: 4, pt: 3, pb: 3.5 }}>

        {/* Validity strip */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 1.5,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <CalendarTodayIcon sx={{ color: '#c9a84c', fontSize: 18, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#8b9bb4', fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Validity Period
            </Typography>
            <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
              {formatDate(valid_from)} &nbsp;→&nbsp; {formatDate(valid_to)}
              {daysUntilExpiry !== null && (
                <Typography component="span" variant="caption" sx={{ ml: 1.5, color: statusColor, fontWeight: 600 }}>
                  {isExpired
                    ? `(expired ${Math.abs(daysUntilExpiry)} days ago)`
                    : `(${daysUntilExpiry} days remaining)`}
                </Typography>
              )}
            </Typography>
          </Box>
          <VerifiedIcon sx={{ color: statusColor, fontSize: 20, flexShrink: 0 }} />
        </Box>

        <Divider sx={{ borderColor: 'rgba(201,168,76,0.15)', mb: 3 }} />

        <Grid container spacing={3}>

          {/* ── Left column ── */}
          <Grid item xs={12} md={6}>

            {/* Crypto */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <FingerprintIcon sx={{ color: '#c9a84c', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#c9a84c', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                Cryptographic Details
              </Typography>
            </Box>

            <CertField label="Key Algorithm" value={key_algorithm} />
            <CertField label="Key Size" value={key_size ? `${key_size} bits` : null} />
            <CertField label="Serial Number" value={serial_number} mono truncate />
            {thumbprint && (
              <Tooltip title={thumbprint} placement="top">
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8b9bb4', fontWeight: 600, fontSize: '0.63rem', mb: 0.3 }}>
                    SHA-1 Thumbprint
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: '"Roboto Mono","Courier New",monospace', color: '#e2e8f0', fontSize: '0.7rem', wordBreak: 'break-all' }}>
                    {thumbprint}
                  </Typography>
                </Box>
              </Tooltip>
            )}

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 2 }} />

            {/* Issuer */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <SecurityIcon sx={{ color: '#c9a84c', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#c9a84c', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                Certificate Authority
              </Typography>
            </Box>
            <CertField label="Issuer" value={issuer} truncate />
            <CertField label="Certificate DN" value={certificate_dn} mono truncate />
          </Grid>

          {/* ── Right column ── */}
          <Grid item xs={12} md={6}>

            {/* Onboarding context */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <SecurityIcon sx={{ color: '#c9a84c', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#c9a84c', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                Onboarding Context
              </Typography>
            </Box>

            <CertField label="Certificate Usage" value={usageLabel(certificate_usage)} />
            <CertField label="Client Name" value={client_name} />
            <CertField label="Environment" value={
              selectedMatch.environment
                ? { prd: 'Production (PRD)', uat: 'UAT', qa: 'QA', dev: 'Development (DEV)' }[selectedMatch.environment]
                  || selectedMatch.environment.toUpperCase()
                : '—'
            } />
            <CertField
              label="Product"
              value={formData.productName || formData.productCode || '—'}
            />
            <CertField label="Requestor Email" value={formData.emailId} />
            <CertField label="Contact Email CR" value={contact_email_cr} />

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 2 }} />

            {/* Venafi policy */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LockIcon sx={{ color: '#c9a84c', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#c9a84c', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                Venafi Policy
              </Typography>
            </Box>
            <CertField label="Policy Base" value={policyBaseFull} mono truncate />
            <CertField label="Additional Path" value={formData.venafiPath || '—'} mono truncate />
          </Grid>

          {/* ── SANs (full width) ── */}
          {sans.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ borderColor: 'rgba(201,168,76,0.15)', mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <DnsIcon sx={{ color: '#c9a84c', fontSize: 16 }} />
                <Typography variant="caption" sx={{ color: '#c9a84c', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Subject Alternative Names ({sans.length})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {sans.map((san, i) => (
                  <Chip
                    key={i}
                    label={san}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(201,168,76,0.1)',
                      border: '1px solid rgba(201,168,76,0.25)',
                      color: '#e2e8f0',
                      fontFamily: '"Roboto Mono","Courier New",monospace',
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* ── Consumers / Devices (full width) ── */}
          {consumers.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ borderColor: 'rgba(201,168,76,0.15)', mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <DevicesIcon sx={{ color: '#c9a84c', fontSize: 16 }} />
                <Typography variant="caption" sx={{ color: '#c9a84c', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Deployed Devices / Consumers ({consumers.length})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {consumers.map((consumer, i) => (
                  <Chip
                    key={i}
                    label={typeof consumer === 'string' ? consumer : JSON.stringify(consumer)}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(100,149,237,0.1)',
                      border: '1px solid rgba(100,149,237,0.25)',
                      color: '#93c5fd',
                      fontFamily: '"Roboto Mono","Courier New",monospace',
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* ─── FOOTER ─────────────────────────────────────────────── */}
      <Box
        sx={{
          borderTop: '1px solid rgba(201,168,76,0.15)',
          background: 'rgba(0,0,0,0.2)',
          px: 4,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" sx={{ color: '#4a5568', fontSize: '0.6rem', letterSpacing: '0.06em' }}>
          SOURCE: VENAFI TPP  •  READ-ONLY PREVIEW
        </Typography>
        {selectedMatch.guid && (
          <Typography variant="caption" sx={{ color: '#4a5568', fontFamily: '"Roboto Mono","Courier New",monospace', fontSize: '0.6rem' }}>
            GUID: {selectedMatch.guid}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default VenafiCertificatePreview
